import React, { useState } from 'react';
import { Search, CheckCircle, XCircle, Loader2, User, Building2 } from 'lucide-react';
import { apiFetch } from '../utils/api';

/** Purpose: Executes { on select, initial npi = '' }. Inputs: { onSelect, initialNpi = '' }. Output: Operation result. */
export default function NpiLookup({ onSelect, initialNpi = '' }) {
  const [npi, setNpi] = useState(initialNpi);
  const [searching, setSearching] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  /** Purpose: Executes verify npi. Inputs: none. Output: Promise with operation result. */
  const verifyNpi = async () => {
    if (!/^\d{10}$/.test(npi)) {
      setError('NPI must be exactly 10 digits');
      return;
    }

    setSearching(true);
    setError('');
    setResult(null);

    try {
      const response = await apiFetch(`/npi/verify/${npi}`);

      const data = await response.json();

      if (!response.ok || !data.found) {
        setError(data.error || 'NPI not found');
        return;
      }

      setResult(data);
    } catch (err) {
      setError('Failed to verify NPI');
    } finally {
      setSearching(false);
    }
  };

  /** Purpose: Handles select. Inputs: none. Output: Operation result. */
  const handleSelect = () => {
    if (result) {
      onSelect({
        npi: result.npi,
        providerName: result.providerName,
        providerType: result.providerType,
        credential: result.credential,
        specialty: result.specialty,
        organization: result.organizationName,
        address: [result.addressLine1, result.addressLine2, [result.city, result.state, result.postalCode].filter(Boolean).join(' ')].filter(Boolean).join(', '),
        phone: result.phone,
        fax: result.fax,
        verified: true
      });
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          NPI Number (Optional)
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            value={npi}
            onChange={(e) => setNpi(e.target.value.replace(/\D/g, '').slice(0, 10))}
            placeholder="Enter 10-digit NPI"
            className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            type="button"
            onClick={verifyNpi}
            disabled={searching || npi.length !== 10}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            {searching ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Search className="w-4 h-4" />
            )}
            Verify
          </button>
        </div>
        <p className="text-xs text-slate-500 mt-1">
          Verifying NPI helps confirm the recipient is a legitimate healthcare provider
        </p>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-red-600 bg-red-50 px-4 py-2 rounded-lg">
          <XCircle className="w-5 h-5" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {result && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <p className="font-medium text-green-900">NPI Verified</p>
                <div className="mt-2 space-y-1 text-sm text-green-800">
                  <div className="flex items-center gap-2">
                    {result.providerType === 'Individual' ? (
                      <User className="w-4 h-4" />
                    ) : (
                      <Building2 className="w-4 h-4" />
                    )}
                    <span className="font-medium">{result.providerName}</span>
                    {result.credential && (
                      <span className="text-green-600">({result.credential})</span>
                    )}
                  </div>
                  {result.specialty && (
                    <p className="text-green-700">{result.specialty}</p>
                  )}
                  {result.city && result.state && (
                    <p className="text-green-700">{result.city}, {result.state}</p>
                  )}
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={handleSelect}
              className="px-3 py-1 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700"
            >
              Use This Provider
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
